import React, { useState } from "react";
import { IoEllipsisVertical } from "react-icons/io5";
import { demoPackages } from "../../assets/DemoData";
import { Button } from "../../components/Reusable/Button";
import SubCategoriesModal from "./Modal/SubCategoriesModal";
import CreatePackageModal from "./Modal/CreatePackageModal";

interface PackageItem {
  id: number;
  image: string;
  product: string;
  quantity: number;
  price: string;
  subcategory: string;
  discount: string;
  discountStatus: boolean;
}

interface ProductPackagesProps {
  productId?: number;
}

const ProductPackages: React.FC<ProductPackagesProps> = ({ productId }) => {
  // Sample data based on the screenshot
  const [packages, setPackages] = useState<PackageItem[]>(demoPackages);
  const [isSubCategoriesModalOpen, setIsSubCategoriesModalOpen] =  useState(false);
  const [isCreatePackageModalOpen, setIsCreatePackageModalOpen] = useState(false);

  const handleCreatePackage = (packageData: {
    subcategory: string;
    name: string;
    quantity: number;
    price: string;
    discount: string;
  }) => {
    // Create a new package object
    const newPackage: PackageItem = {
      id: Date.now(), // Generate a unique ID
      image: "", // You might want to add image handling
      product: packageData.name,
      quantity: packageData.quantity,
      price: packageData.price,
      subcategory: packageData.subcategory,
      discount: packageData.discount,
      discountStatus: false, // Default to false
    };
    
    setPackages([...packages, newPackage]);
  };
  const toggleDiscountStatus = (id: number) => {
    setPackages(
      packages.map((pkg) =>
        pkg.id === id ? { ...pkg, discountStatus: !pkg.discountStatus } : pkg
      )
    );
  };

  return (
    <div className="w-full bg-white">
      <div className="mb-6 p-4 ">
        <div className="flex justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">
            Packages
          </h2>
          <div className="flex items-center gap-4">
            <div className="relative inline-block">
              <button
                className="bg-white dark:bg-[#1E1E1E] font-semibold border border-[#D0D5DD] dark:border-[#FFFFFF1A] text-[#00070B] dark:text-white rounded-lg px-4 py-2 text-base"
                onClick={() => setIsSubCategoriesModalOpen(true)}
              >
                Subcategory
              </button>
            </div>
            <Button
              type="submit"
              bgColor="bg-gradient-to-r from-[#027EC2] to-[#039EF2]"
              textColor="text-white"
              borderRadius="rounded-lg"
              className="w-full h-10"
              onClick={() => setIsCreatePackageModalOpen(true)}
            >
              New Package
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse font-inter">
            <thead>
              <tr className="bg-gray-100 dark:bg-[#FFFFFF1A] text-gray-600 dark:text-white text-xs">
                <th className="px-4 py-3 text-left">Products</th>
                <th className="px-4 py-3 text-left">Quantity</th>
                <th className="px-4 py-3 text-left">Price</th>
                <th className="px-4 py-3 text-left">Subcategory</th>
                <th className="px-4 py-3 text-left">Discount</th>
                <th className="px-4 py-3 text-left">Discount Status</th>
                <th className="px-4 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="text-gray-600 dark:text-white text-sm cursor-pointer">
              {packages.map((pkg) => (
                <tr
                  key={pkg.id}
                  className="border-b border-gray-200 dark:border-[#FFFFFF1A]"
                >
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <img
                        src={pkg.image || "image"}
                        alt={pkg.product}
                        className="w-10 h-10 rounded-md"
                      />
                      <span>{pkg.product}</span>
                    </div>
                  </td>
                  <td className="px-4 py-4">{pkg.quantity}</td>
                  <td className="px-4 py-4">{pkg.price}</td>
                  <td className="px-4 py-4">{pkg.subcategory}</td>
                  <td className="px-4 py-4">{pkg.discount}</td>
                  <td className="px-4 py-4">
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={pkg.discountStatus}
                        onChange={() => toggleDiscountStatus(pkg.id)}
                      />
                      <div className="relative w-11 h-6 bg-gray-200 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-green-500"></div>
                    </label>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <button className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                      <IoEllipsisVertical />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <SubCategoriesModal
          isOpen={isSubCategoriesModalOpen}
          onClose={() => setIsSubCategoriesModalOpen(false)}
          onCreate={(subcategories) => {
            console.log("Created subcategories:", subcategories);
            setIsSubCategoriesModalOpen(false); // Ensure modal closes after creation
          }}
        />
         <CreatePackageModal
          isOpen={isCreatePackageModalOpen}
          onClose={() => setIsCreatePackageModalOpen(false)}
          onCreate={handleCreatePackage}
        />
      </div>
    </div>
  );
};

export default ProductPackages;
